/**
 * client/src/ui/primitives.tsx — the small pieces both faces are built from.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * THE RULE THAT SHAPES EVERY COMPONENT IN THIS FILE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * **No component here contains a user-facing string.** Every label, every `aria-label`, every
 * confirmation word arrives as a prop. That looks like ceremony on `<CopyButton>` — it would be one
 * line shorter with "Copy" and "Copied." baked in — and it is the entire point:
 *
 *   §3.2: "Every family-facing surface is written in `shared/voice/copy.ts` and nowhere else."
 *   VOX-LAW 16b, enforced by `test/no-raw-strings-in-studio.test.ts`: no string literal outside
 *   `copy.ts` reaches the DOM from anything under `client/src/studio/`.
 *
 * The studio agents import these components. A `"Copy"` baked in here would reach the DOM from
 * `client/src/studio/` through this file, pass the grep (because the literal is not *in* `studio/`),
 * and be exactly the defect the grep exists to catch. So the ban is honoured structurally: there is
 * nothing to leak.
 *
 * The two exceptions are marked where they occur and both are console-only: `<SiteMark>` (the
 * product's own name, which is not copy) and `<ConsoleFrame>`'s nav (Karti's face, exempt from the
 * vocabulary rule — `CONSOLE` in `copy.ts` says so).
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * ACCESSIBLE BY CONSTRUCTION, NOT BY AUDIT
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * · Real `<button type="button">` everywhere. There is no `role="button"` in this client. A `div`
 *   with an onClick is not in the tab order, is not in a screen reader's element list, and does not
 *   fire on Space — and the person this product exists for is the person most likely to be using at
 *   least one of those three.
 * · Real `<label htmlFor>`. A placeholder is never the label: it disappears the moment she types,
 *   which on the one screen that asks "Whose voice is this?" means the question vanishes as soon as
 *   she starts answering it.
 * · Sizes in `rem`, `min-height` never `height`. The gate is 200% browser zoom on a 1280 px laptop —
 *   a real setting a real 68-year-old already has on.
 * · `prefers-reduced-motion` is handled once and globally in `tokens.css`, so nothing here has to
 *   remember.
 */

import { Component, useCallback, useEffect, useId, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ComponentProps, ErrorInfo, InputHTMLAttributes, ReactNode } from "react";
import { Link } from "react-router-dom";

// ─── the two shells ──────────────────────────────────────────────────────────

/**
 * The family shell. One centred column, `--stage-max`, and **no header, no nav, no chrome at all.**
 *
 * §3.13: "On the family path she needs three screens — make a voice, wait, hear it — plus a durable
 * way back." A nav bar above a woman being asked to speak is four more things to be uncertain about,
 * and one of them says "Sign in", which §3.1 is explicit is asked about only *after* there is
 * something worth keeping.
 *
 * `<main>` and not `<div>`: it is the landmark a screen reader jumps to, and on a page with no
 * header it is the only one there is.
 *
 * `top` starts the column at the top instead of centring it. Use it for a stage that GROWS — the
 * waiting screen, the keep screen — because a centred column jumps every time an item arrives, and
 * a page that moves under somebody's eyes while they are reading it is a page they lose their place
 * in twice.
 */
export function Stage({
  children,
  top,
  centre,
  wide,
  id = "stage",
}: {
  children: ReactNode;
  top?: boolean;
  /**
   * One moment: a sentence and one or two controls, centred. Every studio screen is one of these.
   *
   * ⚠️ **`centre` and `top` are mutually exclusive in practice and the reason is not taste.** A
   * centred measure is easy to read at one or two lines and hard at five, because a ragged left edge
   * costs the eye a search on every return sweep. Screens that ask a question are short; screens
   * that are a list — the things she said, the claim link, the keep offer — are long. The modifier
   * follows the shape of the content, so `<Stage top>` is never also `centre`.
   */
  centre?: boolean;
  /** The prompt screen only: `--stage-wide` instead of `--stage-max`. Still one column. */
  wide?: boolean;
  id?: string;
}) {
  const cls = ["vox-stage", top ? "vox-stage-top" : "", centre ? "vox-stage-centre" : "", wide ? "vox-stage-wide" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <main id={id} className={cls}>
      <div className="vox-stage-inner">{children}</div>
    </main>
  );
}

/**
 * Karti's shell. Dense, wide, and it may use every internal word.
 *
 * The skip link lives here and not on the stage, and that is not an omission: a skip link exists to
 * jump *over* repeated navigation, and the stage has none. Adding one there would put a control on
 * the door whose only job is to skip a thing that is not there.
 */
export function ConsoleFrame({ children, nav }: { children: ReactNode; nav?: ReactNode }) {
  return (
    <>
      <a className="vox-skip" href="#console">
        Skip to content
      </a>
      <header className="vox-header">
        <div className="vox-header-inner">
          <SiteMark />
          {nav && (
            <nav className="vox-nav" aria-label="Console">
              {nav}
            </nav>
          )}
        </div>
      </header>
      <main id="console" className="vox-console">
        {children}
      </main>
    </>
  );
}

/**
 * The site mark. Console only.
 *
 * The dot is the one accent in the header and it means "this is us" — the same reserved-colour
 * discipline as every sibling, where a second use of the accent in one header is a second meaning
 * nobody agreed on. It is not in `copy.ts` because a domain name is not copy; it is the same string
 * in every language this will ever be read in.
 */
export function SiteMark() {
  return (
    <Link to="/" className="vox-mark">
      <span className="vox-brand" aria-label="Karti Voice">
        <span className="vox-brand-name">KARTI</span>
        <span className="vox-brand-sub">VOICE</span>
      </span>
    </Link>
  );
}

/**
 * The same mark on the door, quiet, and **not a link.**
 *
 * A stranger who has been sent a URL by somebody's grandson needs to know, in the first half-second,
 * what page he is looking at — a naked button on a cream field is not a page, it is a widget. But a
 * link to the page you are already standing on is a control that does nothing, and §3.1's rule is
 * that the door has exactly one control. So it is a `<span>`.
 *
 * Not in `copy.ts` for the reason `<SiteMark>` gives: a domain name is not copy. It is the same
 * string in every language it will ever be read in.
 */
export function Wordmark() {
  return (
    <span className="vox-mark-quiet">
      <span className="vox-brand" aria-label="Karti Voice">
        <span className="vox-brand-name">KARTI</span>
        <span className="vox-brand-sub">VOICE</span>
      </span>
    </span>
  );
}

/**
 * The console's empty state, and there is one because `/live` is opened BEFORE anything happens.
 *
 * That is the entire point of that screen: Karti opens the link his mother sent him and watches an
 * empty page until she presses a button. An em dash in every slot reads as "this page is broken" at
 * exactly the moment he is deciding whether to trust it instead of asking her what her screen says.
 * A sentence reads as "nothing yet", which is both true and useful.
 *
 * It takes its words as a prop, like everything else in this file.
 */
export function Empty({ children }: { children: ReactNode }) {
  return <p className="vox-empty">{children}</p>;
}

/**
 * The third state. Renders nothing, deliberately and visibly nothing — not a spinner.
 *
 * §3.12's argument about the wait screen applies at one-tenth the duration here: "a 68-year-old
 * staring at a spinner concludes it is broken and that it is her fault." A boot request that
 * answers in 40 ms with a spinner in it is a flash of anxiety with no information in it. An empty
 * page for 40 ms is nothing at all, which is the correct amount.
 *
 * It still occupies the viewport so the page does not reflow when the real screen replaces it.
 */
export function Blank() {
  return <div className="vox-stage" aria-hidden="true" />;
}

// ─── buttons ─────────────────────────────────────────────────────────────────

type Variant = "primary" | "plain" | "quiet" | "destructive";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  /** §3.1 — "one button, the size of a hand." Full measure, `--hand` tall, display face. */
  hand?: boolean;
  children: ReactNode;
}

const VARIANT_CLASS: Record<Variant, string> = {
  primary: "vox-btn vox-btn-primary",
  plain: "vox-btn",
  quiet: "vox-btn vox-btn-quiet",
  destructive: "vox-btn vox-btn-destructive",
};

/**
 * ⚠️ **`type="button"` is the default and it is load-bearing.** HTML's default is `submit`, so a
 * button placed inside a form with no explicit type submits it. The name screen is one input and two
 * controls inside a `<form>`; without this, "Back" posts the form.
 */
export function Button({ variant = "plain", hand, className, children, ...rest }: ButtonProps) {
  const cls = [VARIANT_CLASS[variant], hand ? "vox-btn-hand" : "", className ?? ""].filter(Boolean).join(" ");
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  );
}

/** A link that looks like a button. `<a>`, not a `<button>` with a `navigate()` — see below. */
export function LinkButton({
  to,
  variant = "plain",
  hand,
  children,
  ...rest
}: {
  to: string;
  variant?: Variant;
  hand?: boolean;
  children: ReactNode;
} & Omit<ComponentProps<typeof Link>, "to" | "className" | "children">) {
  /**
   * A navigation is an `<a href>` and never a button with an onClick, even when it is styled as a
   * button. The three things that breaks: middle-click and ⌘-click open a new tab, the status bar
   * shows where it goes, and a screen reader announces "link" rather than "button" — which is the
   * difference between "this takes me somewhere" and "this does something to me". On the door,
   * where the one control is the whole product, that distinction is the affordance.
   */
  const cls = [VARIANT_CLASS[variant], hand ? "vox-btn-hand" : ""].filter(Boolean).join(" ");
  return (
    <Link to={to} className={cls} {...rest}>
      {children}
    </Link>
  );
}

// ─── fields ──────────────────────────────────────────────────────────────────

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "id"> {
  /** Rendered in a real `<label htmlFor>`. Required — there is no unlabelled input in this client. */
  label: string;
  /** A sentence under the input. Wired with `aria-describedby`, so it is read with the field. */
  help?: string;
  big?: boolean;
  /**
   * Hide the label visually — **never remove it.**
   *
   * The one legitimate use is a screen whose `<h1>` already asks the question in 32 px, where
   * painting the same sentence again as a form label reads as a mistake. The label element still
   * exists, still carries `htmlFor`, and is still what a screen reader announces when focus lands in
   * the field; `aria-labelledby` pointing at the heading instead would leave the input unlabelled in
   * a form-controls list, which is how a blind user finds fields.
   *
   * It is a prop and not a "just omit the label" affordance, because an optional label is a label
   * somebody forgets.
   */
  labelHidden?: boolean;
}

export function Field({ label, help, big, labelHidden, className, ...rest }: FieldProps) {
  // `useId` and not a hand-rolled counter: two instances of the same field on one page (there is one
  // today, and there will be two on the long-session screen) must not share an id, because a
  // duplicate id silently points every label at the first input.
  const id = useId();
  const helpId = `${id}-help`;
  return (
    <div className="vox-field">
      <label className={labelHidden ? "vox-sr" : "vox-label"} htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className={[big ? "vox-input vox-input-big" : "vox-input", className ?? ""].filter(Boolean).join(" ")}
        aria-describedby={help ? helpId : undefined}
        {...rest}
      />
      {help && (
        <p id={helpId} className="vox-quiet">
          {help}
        </p>
      )}
    </div>
  );
}

// ─── notices ─────────────────────────────────────────────────────────────────

/**
 * A notice. **Its default skin is `--deferred`, not `--destructive`, and that is the design.**
 *
 * §3.14: the deferred path is the common path, not the failure path. spark-1 has no systemd unit on
 * purpose. Painting "we'll have it for you this evening" in caution-amber or error-red would tell a
 * 68-year-old that something went wrong, on the one screen an entire section was written to keep
 * warm.
 *
 * `hard` exists for the console, where a failure really is a failure.
 */
export function Notice({ children, hard }: { children: ReactNode; hard?: boolean }) {
  return <div className={hard ? "vox-notice vox-notice-hard" : "vox-notice"}>{children}</div>;
}

/**
 * A polite live region.
 *
 * `aria-live="polite"` and never `assertive`: assertive interrupts whatever is being read, and on
 * this path the thing being read is the sentence she is about to say aloud. Interrupting that to
 * announce "3 of 5 saved." is worse than not announcing it.
 *
 * It renders its own text visually hidden, so the caller decides separately whether the same words
 * appear on screen. Progress ("{n} of {total} saved.") is announced and shown; a state change like
 * "Copied." is announced and shown; a coach line is shown only, because it is also being *said*.
 */
export function Announce({ children }: { children: ReactNode }) {
  return (
    <div className="vox-sr" role="status" aria-live="polite">
      {children}
    </div>
  );
}

// ─── copy to clipboard ───────────────────────────────────────────────────────

/**
 * §2.4, VOX-LAW 20: the claim link "shown on screen in large type with a copy button".
 *
 * Three things that are not obvious and all three are failures somebody would otherwise ship:
 *
 *  1. **The clipboard write can reject and usually does not tell you why.** It requires a secure
 *     context and, in Safari, a user gesture the browser agrees was a gesture. A button that
 *     silently does nothing on the screen holding the only durable handle to a recording of
 *     somebody's grandmother is the worst button in the product. So a failure selects the text
 *     instead and says so through `onFail`, which the page renders as an instruction she can follow.
 *  2. **The confirmation has to time out.** A "Copied." that stays forever makes the next press look
 *     like it did nothing. 2 s is long enough to read two syllables and short enough that the button
 *     is back before a hand reaches it again.
 *  3. **The timer is cleared on unmount.** Setting state on an unmounted tree is a React warning in
 *     a console nobody is watching, and this component unmounts the moment she presses "Keep it".
 */
export function CopyButton({
  value,
  label,
  copiedLabel,
  onFail,
  variant = "plain",
}: {
  value: string;
  label: string;
  copiedLabel: string;
  /** Called when the clipboard refused. The page decides what to say — the words are in `copy.ts`. */
  onFail?: () => void;
  variant?: Variant;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const copy = useCallback(() => {
    const done = (): void => {
      setCopied(true);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 2000);
    };
    const clipboard = navigator.clipboard;
    if (!clipboard) {
      onFail?.();
      return;
    }
    clipboard.writeText(value).then(done, () => onFail?.());
  }, [value, onFail]);

  return (
    <Button variant={variant} onClick={copy}>
      {copied ? copiedLabel : label}
    </Button>
  );
}

// ─── the waveform ────────────────────────────────────────────────────────────

/**
 * §3.12 — the wait screen "gives her something of her own to look at: her waveforms, and the things
 * she said. Not a spinner, and not a progress bar that lies."
 *
 * Drawn as `<div>` bars, not a `<canvas>`, and the reasons are all accessibility or zoom:
 * a canvas is opaque to assistive technology, needs a device-pixel-ratio dance to not be blurry, and
 * does not reflow at 200%. Flexbox bars do all three for free.
 *
 * `peaks` are normalised 0..1 magnitudes, already reduced to one value per bar by the caller — this
 * component does no DSP, because a component that resamples audio is a component that will be given
 * 24-bit PCM to resample on the main thread while a microphone is open.
 *
 * ⚠️ **The whole figure carries one label and every bar is `aria-hidden`.** "Peak 0.4, peak 0.9,
 * peak 0.6" read out one hundred and twenty times is not information; it is an unusable page.
 *
 * ⚠️ **No number is rendered, and none may be** — VOX-LAW 16: "nothing the gate computes — no band,
 * no score, no similarity number, no percentage — is ever rendered on the family path."
 */
export function Waveform({ peaks, label, empty }: { peaks: readonly number[]; label: string; empty?: string }) {
  if (peaks.length === 0) {
    return (
      <div className="vox-wave-empty" role="img" aria-label={label}>
        {empty ?? null}
      </div>
    );
  }
  return (
    <figure className="vox-wave" role="img" aria-label={label} style={{ margin: 0 }}>
      {peaks.map((p, i) => (
        <span
          key={i}
          className="vox-wave-bar"
          aria-hidden="true"
          // 6% floor: a silent region must still draw a hairline, because a gap in the drawing reads
          // as "it stopped hearing me" — which is exactly the fear §3.4 job 1 exists to answer.
          style={{ height: `${Math.max(6, Math.min(100, p * 100))}%` }}
        />
      ))}
    </figure>
  );
}

// ─── the console's status dot ────────────────────────────────────────────────

/**
 * Three states, and **"asleep" is deliberately distinct from "down".**
 *
 * spark-1 exits 28 on both ports, has no systemd unit on purpose, and is recovered by a human typing
 * `kuda scene activate 115`. That is a different thing from amd-server being unreachable, and it
 * sends Karti to a different place: one is a command, the other is a walk. A two-state dot would
 * paint both red and lose the distinction the whole `/status` page exists to make.
 *
 * The word is always beside the dot. A colour-only status is unreadable to roughly 8% of the men who
 * will ever open this page.
 */
export function StatusDot({ state }: { state: "up" | "asleep" | "down" }) {
  return <span className={`vox-dot vox-dot-${state}`} aria-hidden="true" />;
}

// ─── VOX-LAW 16b, as a boundary ──────────────────────────────────────────────

/**
 * **The last line of VOX-LAW 16b.** "No exception, assertion, or protocol failure anywhere in
 * `client/src/studio/` may render as an error to the person recording. Every one degrades to *'We've
 * got that one — keep going'*, or, if unrecoverable, to the deferred-reveal path, and reports itself
 * to Karti with the session id."
 *
 * A `try/catch` at every call site cannot honour that, because the failure this is really for is the
 * one nobody predicted: a render throw from a component three levels down, which React answers by
 * unmounting the entire tree and painting a white page. A white page is the single worst outcome in
 * this product — it is indistinguishable from "the computer broke" and it has no next step.
 *
 * So: one boundary, wrapping the family path, rendering one warm sentence and one control. It takes
 * its sentence as a prop because the words live in `copy.ts` (see this file's header).
 *
 * ⚠️ **It is a class component, and that is not legacy.** React 19 still provides no hook equivalent
 * of `componentDidCatch`; `useErrorBoundary` does not exist. Rewriting this as a function component
 * is not a modernisation, it is a deletion.
 */
export class FamilyBoundary extends Component<
  {
    children: ReactNode;
    /** The one warm sentence. Pass `FAMILY.mishap.keepGoing`. */
    sentence: string;
    /** The control under it. Usually "start again", pointing at the door. */
    action?: ReactNode;
    /** Full engineering detail, for Karti. Never rendered here. */
    onError?: (where: string, message: string, stack: string | null) => void;
  },
  { failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // The console end of VOX-LAW 16b. `console.error` is the floor, not the ceiling: `/live`
    // installs a real sink through `onError`, and that is the screen the person who can act on it is
    // actually looking at.
    this.props.onError?.("render", error.message, error.stack ?? info.componentStack ?? null);
    if (!this.props.onError) console.error("family boundary caught", error, info.componentStack);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <Stage>
        <p className="vox-say">{this.props.sentence}</p>
        {this.props.action}
      </Stage>
    );
  }
}
